import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    message,
    Popconfirm,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Tabs,
    Typography
} from 'antd';
import { EyeOutlined, ReloadOutlined, SafetyOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import { Upload } from 'antd';
import { connect } from 'react-redux';
import OrganizationStaffManagement from './OrganizationStaffManagement';
import OrganizationMedicalFeeManagement from './OrganizationMedicalFeeManagement';
import {
    getOrganizationLegalDocuments,
    getOrganizationManagementStaffAudit,
    getGlobalDocumentTypes,
    getOrganizationProcedureDocuments,
    getOrganizationProcedureDocumentFile,
    postOrganizationProcedureDocument,
    putOrganizationProcedureDocument,
    inactivateOrganizationProcedureDocument
} from '../store/user/thunks';

const { Text, Title } = Typography;
type AnyRecord = Record<string, any>;

interface OrganizationAccessManagementProps {
    organizationId: string;
    organizationName?: string;
    getOrganizationLegalDocuments?: any;
    getOrganizationManagementStaffAudit?: any;
    getGlobalDocumentTypes?: any;
    getOrganizationProcedureDocuments?: any;
    getOrganizationProcedureDocumentFile?: any;
    postOrganizationProcedureDocument?: any;
    putOrganizationProcedureDocument?: any;
    inactivateOrganizationProcedureDocument?: any;
}

interface OrganizationLegalDocument {
    id: string;
    legalDocumentId: string;
    code: string;
    name: string;
    url?: string;
    description?: string | null;
    status: string;
    optional: boolean;
    current: boolean;
    version: string;
    contentText?: string;
    contentType?: string;
    effectiveFrom?: string;
    acceptedAt?: string;
}

interface OrganizationProcedureDocument {
    id: string;
    name: string;
    description?: string | null;
    url?: string | null;
    status: string;
    daysValidity: number;
    typeDocumentsId: string;
    typeDocumentName: string;
    typeDocumentCode: string;
    format: string;
    template?: string | null;
}

interface GlobalDocumentType {
    id: string;
    name: string;
    code: string;
    template?: string | null;
}

interface OrganizationAuditChange {
    field: string;
    label: string;
    before: any;
    after: any;
}

interface OrganizationStaffAudit {
    id: string;
    entityType: string;
    targetDisplayName: string;
    actorName: string;
    actorEmail?: string;
    action: string;
    outcome: string;
    summary: string;
    changes: OrganizationAuditChange[];
    createdAt: string;
}

interface OrganizationAuditFilters {
    from: string;
    to: string;
    actor: string;
    target: string;
    action?: string;
    entityType?: string;
    search: string;
}

interface OrganizationAuditPage {
    items: OrganizationStaffAudit[];
    total: number;
    page: number;
    pageSize: number;
}

const pick = (source: AnyRecord | null | undefined, ...keys: string[]) => {
    if (!source) return undefined;
    for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null) return source[key];
    }
    return undefined;
};

const normalizeDocuments = (source: AnyRecord | AnyRecord[] | null | undefined): OrganizationLegalDocument[] => {
    const values = Array.isArray(source) ? source : pick(source, 'data', 'items', 'documents', 'Documents');
    return Array.isArray(values)
        ? values.map((item: AnyRecord) => ({
            id: String(pick(item, 'id', 'Id') || ''),
            legalDocumentId: String(pick(item, 'legalDocumentId', 'LegalDocumentId') || ''),
            code: String(pick(item, 'code', 'Code') || ''),
            name: String(pick(item, 'name', 'Name') || ''),
            url: pick(item, 'url', 'Url'),
            description: pick(item, 'description', 'Description'),
            status: String(pick(item, 'status', 'Status') || ''),
            optional: Boolean(pick(item, 'optional', 'Optional')),
            current: Boolean(pick(item, 'current', 'Current')),
            version: String(pick(item, 'version', 'Version') || ''),
            contentText: String(pick(item, 'contentText', 'ContentText') || ''),
            contentType: String(pick(item, 'contentType', 'ContentType') || 'TEXT'),
            effectiveFrom: pick(item, 'effectiveFrom', 'EffectiveFrom'),
            acceptedAt: pick(item, 'acceptedAt', 'AcceptedAt')
        })).filter(item => item.id || item.legalDocumentId)
        : [];
};

const normalizeProcedureDocuments = (source: AnyRecord | AnyRecord[] | null | undefined): OrganizationProcedureDocument[] => {
    const values = Array.isArray(source) ? source : pick(source, 'data', 'items', 'documents', 'Documents');
    return Array.isArray(values)
        ? values.map((item: AnyRecord) => ({
            id: String(pick(item, 'id', 'Id') || ''),
            name: String(pick(item, 'name', 'Name') || ''),
            description: pick(item, 'description', 'Description'),
            url: pick(item, 'url', 'Url'),
            status: String(pick(item, 'status', 'Status') || ''),
            daysValidity: Number(pick(item, 'daysValidity', 'DaysValidity') || 0),
            typeDocumentsId: String(pick(item, 'typeDocumentsId', 'TypeDocumentsId') || ''),
            typeDocumentName: String(pick(item, 'typeDocumentName', 'TypeDocumentName') || ''),
            typeDocumentCode: String(pick(item, 'typeDocumentCode', 'TypeDocumentCode') || ''),
            format: String(pick(item, 'format', 'Format') || ''),
            template: pick(item, 'template', 'Template')
        })).filter(item => item.id)
        : [];
};

const normalizeGlobalDocumentTypes = (source: AnyRecord | AnyRecord[] | null | undefined): GlobalDocumentType[] => {
    const values = Array.isArray(source) ? source : pick(source, 'data', 'items', 'documents', 'Documents');
    return Array.isArray(values)
        ? values.map((item: AnyRecord) => ({
            id: String(pick(item, 'id', 'Id') || ''),
            name: String(pick(item, 'name', 'Name') || ''),
            code: String(pick(item, 'code', 'Code') || ''),
            template: pick(item, 'template', 'Template')
        })).filter(item => item.id)
        : [];
};

const formatDate = (value?: string) => {
    if (!value) return 'No disponible';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString('es-EC');
};

const renderProcedureTemplatePreview = (template?: string | null) => {
    const values: Record<string, string> = {
        PACIENTE_NOMBRE: 'Paciente de ejemplo',
        PACIENTE_IDENTIFICACION: '9999999999',
        HISTORIA_CLINICA: 'HC-DEMO-0001',
        PACIENTE_FECHA_NACIMIENTO: '01/01/1985',
        PACIENTE_GENERO: 'No especificado',
        ATENCION_CODIGO: 'AT-DEMO-0001',
        ATENCION_FECHA: '01/01/2026 09:30',
        FECHA_EMISION: '01/01/2026',
        MEDICO_NOMBRE: 'Dr./Dra. de ejemplo',
        MEDICO_ESPECIALIDAD: 'Especialidad de ejemplo',
        CENTRO_NOMBRE: 'Centro de ejemplo',
        PRODUCTO_NOMBRE: 'Procedimiento de ejemplo',
        DIAGNOSTICOS: 'Diagnóstico proveniente de la ficha médica',
        ORDENES_MEDICAS: 'Orden médica de ejemplo',
        SIGNOS_VITALES: 'PA 120/80 mmHg · FC 72 lpm · Temp. 36.5 °C',
        RESULTADOS_PREVIOS: 'Sin resultados previos'
    };
    let html = template || '<div style="padding:24px;color:#6b7280;">El tipo global no tiene una plantilla HTML configurada.</div>';
    html = html.replace(/<script[\s\S]*?<\/script>/gi, '');
    html = html.replace(/\son[a-z]+\s*=\s*(["']).*?\1/gi, '');
    html = html.replace(/\{\{([A-Z0-9_]+)\}\}/g, (_match, key: string) => values[key] || `[${key}]`);
    html = html.replace(/data-field="([^"]+)"/gi, 'data-preview-field="$1"');
    html = html.replace(/(<(?:div|span|td|p)[^>]*data-preview-field="([^"]+)"[^>]*)>(\s*<\/[^>]+>|\s*)/gi, (_match, opening: string, key: string) => `${opening}<span style="display:inline-block;min-width:160px;min-height:24px;padding:3px 7px;border:1px dashed #1677ff;background:#e6f4ff;color:#0958d9;">Campo editable: ${key}</span>`);
    return `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:24px;background:white;font-family:Arial,sans-serif;}table{max-width:100%;}img{max-width:100%;}</style></head><body>${html}</body></html>`;
};

const normalizeAudit = (source: AnyRecord | AnyRecord[] | null | undefined): OrganizationStaffAudit[] => {
    const values = Array.isArray(source) ? source : pick(source, 'data', 'items', 'audit', 'logs', 'Audit', 'Logs');
    return Array.isArray(values)
        ? values.map((item: AnyRecord) => {
            const changes = pick(item, 'changes', 'Changes');
            return {
                id: String(pick(item, 'id', 'Id') || `${pick(item, 'createdAt', 'CreatedAt') || ''}-${pick(item, 'targetUserId', 'TargetUserId') || ''}`),
                entityType: String(pick(item, 'entityType', 'EntityType') || 'ORGANIZATION_STAFF'),
                targetDisplayName: String(pick(item, 'targetDisplayName', 'TargetDisplayName') || 'Usuario sin nombre'),
                actorName: String(pick(item, 'actorName', 'ActorName') || 'Usuario no disponible'),
                actorEmail: pick(item, 'actorEmail', 'ActorEmail'),
                action: String(pick(item, 'action', 'Action') || 'ACTUALIZAR'),
                outcome: String(pick(item, 'outcome', 'Outcome') || 'SUCCESS'),
                summary: String(pick(item, 'summary', 'Summary') || ''),
                changes: Array.isArray(changes) ? changes.map((change: AnyRecord) => ({
                    field: String(pick(change, 'field', 'Field') || ''),
                    label: String(pick(change, 'label', 'Label') || pick(change, 'field', 'Field') || 'Cambio'),
                    before: pick(change, 'before', 'Before'),
                    after: pick(change, 'after', 'After')
                })) : [],
                createdAt: String(pick(item, 'createdAt', 'CreatedAt') || '')
            };
        })
        : [];
};

const normalizeAuditPage = (source: AnyRecord | AnyRecord[] | null | undefined): OrganizationAuditPage => {
    if (Array.isArray(source)) {
        const items = normalizeAudit(source);
        return { items, total: items.length, page: 1, pageSize: items.length || 20 };
    }
    const wrapped = pick(source, 'data', 'Data');
    const container = wrapped && !Array.isArray(wrapped) ? wrapped : source;
    const values = Array.isArray(wrapped)
        ? wrapped
        : pick(container, 'items', 'Items', 'audit', 'logs', 'Audit', 'Logs') || [];
    const items = normalizeAudit(values);
    return {
        items,
        total: Number(pick(container, 'total', 'Total') ?? items.length),
        page: Number(pick(container, 'page', 'Page') ?? 1),
        pageSize: Number(pick(container, 'pageSize', 'PageSize') ?? 20)
    };
};

const formatAuditValue = (value: any) => {
    if (value === null || value === undefined || value === '') return 'Sin valor';
    if (Array.isArray(value)) return value.length ? value.join(', ') : 'Sin elementos';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

const OrganizationAccessManagement: React.FC<OrganizationAccessManagementProps> = props => {
    const [documents, setDocuments] = useState<OrganizationLegalDocument[]>([]);
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [preview, setPreview] = useState<OrganizationLegalDocument | null>(null);
    const [audit, setAudit] = useState<OrganizationStaffAudit[]>([]);
    const [loadingAudit, setLoadingAudit] = useState(false);
    const [auditTotal, setAuditTotal] = useState(0);
    const [auditPage, setAuditPage] = useState(1);
    const [auditPageSize, setAuditPageSize] = useState(20);
    const [auditFilters, setAuditFilters] = useState<OrganizationAuditFilters>({
        from: '', to: '', actor: '', target: '', action: '', entityType: 'ALL', search: ''
    });
    const [procedureDocuments, setProcedureDocuments] = useState<OrganizationProcedureDocument[]>([]);
    const [documentTypes, setDocumentTypes] = useState<GlobalDocumentType[]>([]);
    const [loadingProcedureDocuments, setLoadingProcedureDocuments] = useState(false);
    const [procedureDocumentModal, setProcedureDocumentModal] = useState(false);
    const [htmlPreviewTemplate, setHtmlPreviewTemplate] = useState<string | null>(null);
    const [editingProcedureDocument, setEditingProcedureDocument] = useState<OrganizationProcedureDocument | null>(null);
    const [pdfFile, setPdfFile] = useState<File | null>(null);
    const [pdfFileList, setPdfFileList] = useState<UploadFile[]>([]);
    const [procedureDocumentForm] = Form.useForm();

    const loadDocuments = useCallback(async () => {
        setLoadingDocuments(true);
        try {
            const response = await props.getOrganizationLegalDocuments?.(props.organizationId);
            setDocuments(normalizeDocuments(response));
        } finally {
            setLoadingDocuments(false);
        }
    }, [props.getOrganizationLegalDocuments, props.organizationId]);

    const loadAudit = useCallback(async (
        requestedPage = auditPage,
        requestedFilters = auditFilters,
        requestedPageSize = auditPageSize
    ) => {
        setLoadingAudit(true);
        try {
            const response = await props.getOrganizationManagementStaffAudit?.(props.organizationId, {
                ...requestedFilters,
                page: requestedPage,
                pageSize: requestedPageSize
            });
            const result = normalizeAuditPage(response);
            setAudit(result.items);
            setAuditTotal(result.total);
            setAuditPage(result.page || requestedPage);
            setAuditPageSize(result.pageSize || requestedPageSize);
        } finally {
            setLoadingAudit(false);
        }
    }, [auditFilters, auditPage, auditPageSize, props.getOrganizationManagementStaffAudit, props.organizationId]);

    const loadProcedureDocuments = useCallback(async () => {
        setLoadingProcedureDocuments(true);
        try {
            const [documentsResponse, typesResponse] = await Promise.all([
                props.getOrganizationProcedureDocuments?.(props.organizationId),
                props.getGlobalDocumentTypes?.()
            ]);
            setProcedureDocuments(normalizeProcedureDocuments(documentsResponse));
            setDocumentTypes(normalizeGlobalDocumentTypes(typesResponse));
        } finally {
            setLoadingProcedureDocuments(false);
        }
    }, [props.getGlobalDocumentTypes, props.getOrganizationProcedureDocuments, props.organizationId]);

    useEffect(() => {
        loadDocuments();
        loadProcedureDocuments();
        loadAudit(1, auditFilters, auditPageSize);
        // Los filtros se aplican únicamente al pulsar Buscar; no se debe consultar
        // el backend en cada tecla mientras el usuario los está preparando.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadDocuments, loadProcedureDocuments, props.organizationId]);

    const openProcedureDocument = (document?: OrganizationProcedureDocument) => {
        setEditingProcedureDocument(document || null);
        const selectedType = document
            ? documentTypes.find(type => type.id === document.typeDocumentsId)
            : undefined;
        const suggestedFormat = selectedType
            ? (selectedType.template?.trim() ? 'HTML' : 'PDF')
            : (document?.format || 'PDF');
        const selectedFormat = document?.format || suggestedFormat;
        const globalTemplate = selectedType?.template?.trim() || '';
        const organizationTemplate = document?.template?.trim() || '';
        const hasCustomTemplate = Boolean(
            selectedFormat === 'HTML'
            && ((!globalTemplate) || (organizationTemplate && organizationTemplate !== globalTemplate))
        );
        procedureDocumentForm.setFieldsValue(document ? {
            name: document.name,
            description: document.description || '',
            typeDocumentsId: document.typeDocumentsId,
            format: selectedFormat,
            customHtmlTemplate: hasCustomTemplate,
            template: hasCustomTemplate ? organizationTemplate : '',
            status: document.status,
            daysValidity: document.daysValidity,
            url: document.url || ''
        } : {
            name: '', description: '', typeDocumentsId: undefined, format: 'PDF',
            customHtmlTemplate: false, template: '', status: 'ACTIVO', daysValidity: 365
        });
        setPdfFile(null);
        setPdfFileList([]);
        setProcedureDocumentModal(true);
    };

    const closeProcedureDocument = () => {
        setProcedureDocumentModal(false);
        setEditingProcedureDocument(null);
        setPdfFile(null);
        setPdfFileList([]);
        procedureDocumentForm.resetFields();
    };

    const openHtmlTemplatePreview = () => {
        const typeDocumentsId = procedureDocumentForm.getFieldValue('typeDocumentsId');
        const selectedType = documentTypes.find(type => type.id === typeDocumentsId);
        const useCustomTemplate = Boolean(procedureDocumentForm.getFieldValue('customHtmlTemplate'));
        const customTemplate = String(procedureDocumentForm.getFieldValue('template') || '').trim();
        const template = useCustomTemplate || !selectedType?.template?.trim()
            ? customTemplate
            : selectedType.template;
        if (!template) {
            message.warning('Defina una plantilla HTML para poder ver la vista previa.');
            return;
        }
        setHtmlPreviewTemplate(template);
    };

    const readFileAsBase64 = (file: File) => new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('No se pudo leer el archivo PDF.'));
        reader.readAsDataURL(file);
    });

    const submitProcedureDocument = async (values: AnyRecord) => {
        const customTemplate = String(values.template || '').trim();
        const selectedType = documentTypes.find(type => type.id === values.typeDocumentsId);
        const globalTemplate = selectedType?.template?.trim() || '';
        const useCustomTemplate = values.format === 'HTML'
            && (Boolean(values.customHtmlTemplate) || !globalTemplate);
        if (values.format === 'HTML' && useCustomTemplate && !customTemplate && !globalTemplate) {
            procedureDocumentForm.setFields([{ name: 'template', errors: ['Ingrese la plantilla HTML personalizada.'] }]);
            return;
        }
        if (values.format === 'PDF' && !pdfFile && !editingProcedureDocument?.url) {
            procedureDocumentForm.setFields([{ name: 'pdfFile', errors: ['Debe cargar un archivo PDF.'] }]);
            return;
        }

        const pdfBase64 = pdfFile ? await readFileAsBase64(pdfFile) : undefined;
        const payload = {
            name: values.name,
            description: values.description || null,
            typeDocumentsId: values.typeDocumentsId,
            format: values.format,
            status: values.status,
            daysValidity: Number(values.daysValidity),
            template: values.format === 'HTML' && useCustomTemplate
                ? customTemplate
                : undefined,
            pdfBase64,
            pdfFileName: pdfFile?.name,
            pdfContentType: pdfFile?.type || 'application/pdf'
        };
        const response = editingProcedureDocument
            ? await props.putOrganizationProcedureDocument?.(props.organizationId, editingProcedureDocument.id, payload)
            : await props.postOrganizationProcedureDocument?.(props.organizationId, payload);
        if (response !== undefined) {
            // POST/PUT devuelve la colección completa de documentos operativos.
            // La pantalla la usa directamente y evita un GET inmediatamente
            // después de guardar.
            setProcedureDocuments(normalizeProcedureDocuments(response));
            closeProcedureDocument();
        }
    };

    const inactivateProcedureDocument = async (document: OrganizationProcedureDocument) => {
        const response = await props.inactivateOrganizationProcedureDocument?.(props.organizationId, document.id);
        if (response !== undefined) {
            // La inactivación es una mutación de la misma colección.
            setProcedureDocuments(normalizeProcedureDocuments(response));
        }
    };

    const viewProcedureDocumentFile = async (document: OrganizationProcedureDocument) => {
        const response = await props.getOrganizationProcedureDocumentFile?.(props.organizationId, document.id);
        const sasUrl = pick(response, 'sasUrl', 'SasUrl', 'url', 'Url');
        if (sasUrl) {
            window.open(String(sasUrl), '_blank', 'noopener,noreferrer');
        }
    };

    const documentColumns = [
        {
            title: 'Documento',
            key: 'document',
            render: (_: unknown, row: OrganizationLegalDocument) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{row.name || row.code}</Text>
                    <Text type="secondary">{row.code}</Text>
                </Space>
            )
        },
        { title: 'Versión', dataIndex: 'version', key: 'version' },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            render: (value: string) => <Tag color={value === 'ACTIVO' ? 'green' : 'default'}>{value || 'SIN ESTADO'}</Tag>
        },
        {
            title: 'Tipo',
            key: 'type',
            render: (_: unknown, row: OrganizationLegalDocument) => row.optional ? 'Opcional' : 'Obligatorio'
        },
        {
            title: 'Aceptado',
            dataIndex: 'acceptedAt',
            key: 'acceptedAt',
            render: (value: string) => formatDate(value)
        },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'right' as const,
            render: (_: unknown, row: OrganizationLegalDocument) => (
                <Button icon={<EyeOutlined />} onClick={() => setPreview(row)}>Ver documento</Button>
            )
        }
    ];

    const documentsSection = (
        <Card
            title="Documentos asignados a la organización"
            extra={<Button icon={<ReloadOutlined />} onClick={loadDocuments} loading={loadingDocuments}>Actualizar</Button>}
        >
            <Alert
                type="info"
                showIcon
                message="Contexto administrativo de Blue"
                description="Esta consulta se realiza con el organizationId seleccionado en Blue. La asignación y aceptación del documento se conserva en Identity; aquí se muestra la evidencia asociada a esta organización."
                style={{ marginBottom: 16 }}
            />
            {loadingDocuments ? <Spin /> : documents.length ? (
                <Table rowKey={row => row.id || row.legalDocumentId} columns={documentColumns} dataSource={documents} pagination={{ pageSize: 8 }} scroll={{ x: 900 }} />
            ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay documentos asignados" />
            )}
        </Card>
    );

    const procedureDocumentColumns = [
        {
            title: 'Documento operativo',
            key: 'document',
            render: (_: unknown, row: OrganizationProcedureDocument) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{row.name}</Text>
                    <Text type="secondary">{row.typeDocumentCode || row.typeDocumentName}</Text>
                </Space>
            )
        },
        {
            title: 'Vigencia al emitir',
            key: 'daysValidity',
            render: (_: unknown, row: OrganizationProcedureDocument) => `${row.daysValidity} día${row.daysValidity === 1 ? '' : 's'} desde la emisión`
        },
        { title: 'Formato', dataIndex: 'format', key: 'format', render: (value: string) => <Tag>{value}</Tag> },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            render: (value: string) => <Tag color={value === 'ACTIVO' ? 'green' : 'default'}>{value || 'SIN ESTADO'}</Tag>
        },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'right' as const,
            render: (_: unknown, row: OrganizationProcedureDocument) => (
                <Space>
                    {row.format === 'PDF' && <Button onClick={() => viewProcedureDocumentFile(row)}>Ver PDF</Button>}
                    <Button onClick={() => openProcedureDocument(row)}>Editar</Button>
                    {row.status === 'ACTIVO' && <Popconfirm
                        title="¿Inactivar este documento?"
                        description="Dejará de estar disponible para nuevas asignaciones."
                        okText="Inactivar"
                        cancelText="Cancelar"
                        onConfirm={() => inactivateProcedureDocument(row)}
                    >
                        <Button danger>Inactivar</Button>
                    </Popconfirm>}
                </Space>
            )
        }
    ];

    const procedureDocumentsSection = (
        <Card
            title="Documentos operativos de la organización"
            extra={<Space>
                <Button icon={<ReloadOutlined />} onClick={loadProcedureDocuments} loading={loadingProcedureDocuments}>Actualizar</Button>
                <Button type="primary" onClick={() => openProcedureDocument()}>Nuevo documento</Button>
            </Space>}
        >
            <Alert
                type="info"
                showIcon
                message="Configuración de pdt.documents"
                description="Registra los documentos que esta organización utilizará en sus procedimientos. El tipo se selecciona del catálogo global pdt.type_documents y no se crea otro catálogo por organización."
                style={{ marginBottom: 16 }}
            />
            {loadingProcedureDocuments ? <Spin /> : procedureDocuments.length ? (
                <Table rowKey="id" columns={procedureDocumentColumns} dataSource={procedureDocuments} pagination={{ pageSize: 8 }} scroll={{ x: 1000 }} />
            ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay documentos operativos configurados" />
            )}
        </Card>
    );

    const auditColumns = [
        {
            title: 'Fecha',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 170,
            render: (value: string) => formatDate(value)
        },
        {
            title: 'Acción',
            dataIndex: 'action',
            key: 'action',
            width: 120,
            render: (value: string, row: OrganizationStaffAudit) => (
                <Tag color={row.outcome === 'SUCCESS' ? 'blue' : 'red'}>{value}</Tag>
            )
        },
        {
            title: 'Tipo',
            dataIndex: 'entityType',
            key: 'entityType',
            width: 150,
            render: (value: string) => value === 'ORGANIZATION_STAFF' ? 'Personal' : value === 'ORGANIZATION_DOCUMENT' ? 'Documento' : value
        },
        {
            title: 'Administrador',
            key: 'actor',
            render: (_: unknown, row: OrganizationStaffAudit) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{row.actorName}</Text>
                    {row.actorEmail && <Text type="secondary">{row.actorEmail}</Text>}
                </Space>
            )
        },
        {
            title: 'Usuario afectado',
            dataIndex: 'targetDisplayName',
            key: 'targetDisplayName'
        },
        {
            title: 'Resumen',
            dataIndex: 'summary',
            key: 'summary',
            render: (value: string) => value || 'Sin descripción'
        },
        {
            title: 'Cambios',
            key: 'changes',
            render: (_: unknown, row: OrganizationStaffAudit) => row.changes.length ? (
                <Space direction="vertical" size={2}>
                    {row.changes.map((change, index) => (
                        <Text key={`${row.id}-${change.field}-${index}`}>
                            <Text strong>{change.label}:</Text> {formatAuditValue(change.before)} → {formatAuditValue(change.after)}
                        </Text>
                    ))}
                </Space>
            ) : <Text type="secondary">Sin cambios detallados</Text>
        }
    ];

    const auditSection = (
        <Card
            title="Bitácora administrativa"
            extra={<Button icon={<ReloadOutlined />} onClick={() => loadAudit(auditPage, auditFilters, auditPageSize)} loading={loadingAudit}>Actualizar</Button>}
        >
            <Alert
                type="info"
                showIcon
                message="Trazabilidad administrativa"
                description="Consulta la trazabilidad sin cargar todo el histórico. Los filtros y la paginación se ejecutan en el backend; la vista es resumida y legible para la organización."
                style={{ marginBottom: 16 }}
            />
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={24} md={8} lg={6}>
                    <Input
                        placeholder="Buscar en resumen, usuario o acción"
                        value={auditFilters.search}
                        onChange={event => setAuditFilters(current => ({ ...current, search: event.target.value }))}
                        allowClear
                    />
                </Col>
                <Col xs={24} md={8} lg={5}>
                    <Input
                        placeholder="Administrador"
                        value={auditFilters.actor}
                        onChange={event => setAuditFilters(current => ({ ...current, actor: event.target.value }))}
                        allowClear
                    />
                </Col>
                <Col xs={24} md={8} lg={5}>
                    <Input
                        placeholder="Usuario o elemento afectado"
                        value={auditFilters.target}
                        onChange={event => setAuditFilters(current => ({ ...current, target: event.target.value }))}
                        allowClear
                    />
                </Col>
                <Col xs={12} md={5} lg={3}>
                    <Input type="date" value={auditFilters.from} onChange={event => setAuditFilters(current => ({ ...current, from: event.target.value }))} />
                </Col>
                <Col xs={12} md={5} lg={3}>
                    <Input type="date" value={auditFilters.to} onChange={event => setAuditFilters(current => ({ ...current, to: event.target.value }))} />
                </Col>
                <Col xs={12} md={5} lg={4}>
                    <Select
                        value={auditFilters.action || undefined}
                        placeholder="Todas las acciones"
                        onChange={value => setAuditFilters(current => ({ ...current, action: value }))}
                        allowClear
                        style={{ width: '100%' }}
                    >
                        <Select.Option value="CREAR">Crear</Select.Option>
                        <Select.Option value="ACTUALIZAR">Actualizar</Select.Option>
                        <Select.Option value="INACTIVAR">Inactivar</Select.Option>
                    </Select>
                </Col>
                <Col xs={12} md={5} lg={4}>
                    <Select
                        value={auditFilters.entityType || 'ALL'}
                        onChange={value => setAuditFilters(current => ({ ...current, entityType: value }))}
                        style={{ width: '100%' }}
                    >
                        <Select.Option value="ALL">Todos los tipos</Select.Option>
                        <Select.Option value="ORGANIZATION_STAFF">Personal</Select.Option>
                        <Select.Option value="ORGANIZATION_DOCUMENT">Documentos</Select.Option>
                    </Select>
                </Col>
                <Col xs={24} md={6} lg={4}>
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setAuditPage(1);
                                loadAudit(1, auditFilters, auditPageSize);
                            }}
                            loading={loadingAudit}
                        >
                            Buscar
                        </Button>
                        <Button
                            onClick={() => {
                                const cleared: OrganizationAuditFilters = { from: '', to: '', actor: '', target: '', action: '', entityType: 'ALL', search: '' };
                                setAuditFilters(cleared);
                                setAuditPage(1);
                                loadAudit(1, cleared, auditPageSize);
                            }}
                        >
                            Limpiar
                        </Button>
                    </Space>
                </Col>
            </Row>
            {loadingAudit ? <Spin /> : audit.length ? (
                <Table
                    rowKey="id"
                    columns={auditColumns}
                    dataSource={audit}
                    pagination={{
                        current: auditPage,
                        pageSize: auditPageSize,
                        total: auditTotal,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: total => `${total} movimientos`,
                        onChange: (page, pageSize) => {
                            setAuditPage(page);
                            setAuditPageSize(pageSize);
                            loadAudit(page, auditFilters, pageSize);
                        }
                    }}
                    scroll={{ x: 1350 }}
                />
            ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aún no hay movimientos registrados" />
            )}
        </Card>
    );

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card>
                <Space align="start">
                    <SafetyOutlined style={{ fontSize: 24, color: '#1677ff' }} />
                    <div>
                        <Title level={4} style={{ margin: 0 }}>Usuarios, roles y documentación</Title>
                        <Text type="secondary">Administra el acceso de {props.organizationName || 'la organización'}, sus documentos legales y sus documentos operativos.</Text>
                    </div>
                </Space>
            </Card>
            <Tabs
                destroyInactiveTabPane
                items={[
                    {
                        key: 'staff',
                        label: 'Usuarios y roles',
                        children: <OrganizationStaffManagement
                            organizationId={props.organizationId}
                            organizationName={props.organizationName}
                            onStaffChanged={loadAudit}
                        />
                    },
                    {
                        key: 'medical-fees',
                        label: 'Honorarios médicos',
                        children: <OrganizationMedicalFeeManagement organizationId={props.organizationId} />
                    },
                    {
                        key: 'documents',
                        label: 'Legales aceptados',
                        children: documentsSection
                    },
                    {
                        key: 'procedure-documents',
                        label: 'Documentos operativos',
                        children: procedureDocumentsSection
                    },
                    {
                        key: 'audit',
                        label: 'Bitácora',
                        children: auditSection
                    }
                ]}
            />
            <Modal
                open={!!preview}
                title={preview ? `${preview.name} · versión ${preview.version}` : 'Documento'}
                footer={null}
                width={860}
                onCancel={() => setPreview(null)}
                destroyOnClose
            >
                {preview && <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Text type="secondary">Código: {preview.code} · Aceptado: {formatDate(preview.acceptedAt)}</Text>
                    {preview.description && <Text>{preview.description}</Text>}
                    <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 480, overflow: 'auto', padding: 16, background: '#f5f5f5', borderRadius: 6 }}>
                        {preview.contentText || 'El documento no tiene contenido textual disponible.'}
                    </pre>
                    {preview.url && <Button type="link" href={preview.url} target="_blank" rel="noreferrer">Abrir URL pública</Button>}
                </Space>}
            </Modal>
            <Modal
                open={procedureDocumentModal}
                title={editingProcedureDocument ? 'Editar documento operativo' : 'Nuevo documento operativo'}
                okText="Guardar"
                cancelText="Cancelar"
                onCancel={closeProcedureDocument}
                onOk={() => procedureDocumentForm.submit()}
                destroyOnClose
                width={760}
            >
                <Alert
                    type="info"
                    showIcon
                    message="Documento de la organización"
                    description="Este formulario administra una fila de pdt.documents. Los tipos disponibles provienen del catálogo global y deben configurarse previamente por Blue."
                    style={{ marginBottom: 16 }}
                />
                <Form form={procedureDocumentForm} layout="vertical" onFinish={submitProcedureDocument}>
                    <Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Ingrese el nombre del documento' }]}>
                        <Input maxLength={180} />
                    </Form.Item>
                    <Form.Item name="description" label="Descripción">
                        <Input.TextArea rows={2} maxLength={500} />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="typeDocumentsId" label="Tipo global" rules={[{ required: true, message: 'Seleccione un tipo global' }]}>
                                <Select
                                    placeholder="Seleccione pdt.type_documents"
                                    options={documentTypes.map(type => ({ value: type.id, label: `${type.name} (${type.code})` }))}
                                    onChange={(value: string) => {
                                        const selectedType = documentTypes.find(type => type.id === value);
                                        procedureDocumentForm.setFieldsValue({
                                            format: selectedType?.template?.trim() ? 'HTML' : 'PDF',
                                            customHtmlTemplate: !selectedType?.template?.trim(),
                                            template: ''
                                        });
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item noStyle shouldUpdate={(previous, current) => previous.typeDocumentsId !== current.typeDocumentsId}>
                                {({ getFieldValue }) => {
                                    const selectedType = documentTypes.find(type => type.id === getFieldValue('typeDocumentsId'));
                                    const hasHtmlTemplate = Boolean(selectedType?.template?.trim());
                                    return (
                                        <Form.Item name="format" label="Formato" rules={[{ required: true }]}>
                                            <Select
                                                disabled={!selectedType}
                                                options={selectedType ? [
                                                    { value: 'HTML', label: hasHtmlTemplate ? 'HTML / plantilla global sugerida' : 'HTML / plantilla propia' },
                                                    { value: 'PDF', label: 'PDF / archivo cargado' }
                                                ] : []}
                                            />
                                        </Form.Item>
                                    );
                                }}
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item name="status" label="Estado" rules={[{ required: true }]}>
                                <Select options={[{ value: 'ACTIVO', label: 'Activo' }, { value: 'INACTIVO', label: 'Inactivo' }]} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="daysValidity" label="Días de vigencia desde la emisión" rules={[{ required: true, message: 'Ingrese los días de vigencia' }]} style={{ maxWidth: 260 }}>
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Alert
                        type="info"
                        showIcon
                        message="La vigencia comienza al generar o imprimir el documento"
                        description="Por ejemplo, con 7 días, el backend toma la fecha de emisión como inicio y calcula automáticamente la fecha de vencimiento."
                        style={{ marginBottom: 16 }}
                    />
                    <Form.Item noStyle shouldUpdate={(previous, current) =>
                        previous.format !== current.format
                        || previous.typeDocumentsId !== current.typeDocumentsId
                        || previous.customHtmlTemplate !== current.customHtmlTemplate
                    }>
                        {({ getFieldValue }) => {
                            const selectedType = documentTypes.find(type => type.id === getFieldValue('typeDocumentsId'));
                            const hasHtmlTemplate = Boolean(selectedType?.template?.trim());
                            return getFieldValue('format') === 'HTML' ? (
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                <Alert
                                    type="success"
                                    showIcon
                                    message="Formato HTML seleccionado"
                                    description={hasHtmlTemplate
                                        ? 'La organización puede utilizar la plantilla genérica de Blue, modificarla o definir una estructura HTML propia. También puede cambiar el formato a PDF.'
                                        : 'Este tipo no tiene una plantilla global; la organización debe definir su propia plantilla HTML o cambiar el formato a PDF.'}
                                />
                                <Form.Item
                                    name="customHtmlTemplate"
                                    valuePropName="checked"
                                    style={{ marginBottom: 0 }}
                                >
                                    <Checkbox disabled={!hasHtmlTemplate}>
                                        {hasHtmlTemplate ? 'Personalizar la plantilla HTML para esta organización' : 'Definir plantilla HTML propia'}
                                    </Checkbox>
                                </Form.Item>
                                {getFieldValue('customHtmlTemplate') || !hasHtmlTemplate ? (
                                    <Form.Item
                                        name="template"
                                        label="Plantilla HTML de la organización"
                                        rules={[{ required: true, message: 'Ingrese la plantilla HTML personalizada' }]}
                                    >
                                        <Input.TextArea
                                            rows={14}
                                            placeholder={'Ingrese HTML y utilice {{TOKEN}} o data-field="CAMPO" para los datos del flujo.'}
                                            maxLength={200000}
                                            showCount
                                        />
                                    </Form.Item>
                                ) : null}
                                <Button icon={<EyeOutlined />} onClick={openHtmlTemplatePreview}>
                                    Ver vista previa HTML
                                </Button>
                            </Space>
                            ) : (
                            <Form.Item label="Archivo PDF informativo" name="pdfFile">
                                <Upload
                                    accept="application/pdf,.pdf"
                                    maxCount={1}
                                    fileList={pdfFileList}
                                    beforeUpload={(file) => {
                                        setPdfFile(file as File);
                                        setPdfFileList([file]);
                                        return false;
                                    }}
                                    onRemove={() => {
                                        setPdfFile(null);
                                        setPdfFileList([]);
                                    }}
                                >
                                    <Button icon={<UploadOutlined />}>Seleccionar PDF</Button>
                                </Upload>
                                <Text type="secondary">El archivo se cargará al Azure Blob Storage de forma segura. No se registra una URL pública.</Text>
                            </Form.Item>
                            );
                        }}
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                open={htmlPreviewTemplate !== null}
                title="Vista previa de plantilla HTML"
                footer={<Button onClick={() => setHtmlPreviewTemplate(null)}>Cerrar</Button>}
                width={960}
                onCancel={() => setHtmlPreviewTemplate(null)}
                destroyOnClose
            >
                <Alert
                    type="info"
                    showIcon
                    message="Vista de solo lectura"
                    description="Los datos mostrados son DEMO. Al generar el informe, los tokens automáticos se reemplazarán con la atención seleccionada y los campos editables serán completados por el profesional."
                    style={{ marginBottom: 12 }}
                />
                <iframe
                    title="Vista previa de plantilla HTML"
                    sandbox=""
                    srcDoc={renderProcedureTemplatePreview(htmlPreviewTemplate)}
                    style={{ width: '100%', height: 620, border: '1px solid #d9d9d9', borderRadius: 6, background: '#fff' }}
                />
            </Modal>
        </Space>
    );
};

export default connect(null, {
    getOrganizationLegalDocuments,
    getOrganizationManagementStaffAudit,
    getGlobalDocumentTypes,
    getOrganizationProcedureDocuments,
    getOrganizationProcedureDocumentFile,
    postOrganizationProcedureDocument,
    putOrganizationProcedureDocument,
    inactivateOrganizationProcedureDocument
})(OrganizationAccessManagement);
